/**
 * Parser Text DSL
 *
 * Składnia:
 * - {fieldName} - podstawienie wartości z danych
 * - {fieldName|transform} - transformacja wartości (upper, lower, quote, suffix:X, default:X)
 * - {fieldName|transform1|transform2} - wielokrotne transformacje
 * - {?fieldName}...{/fieldName} - warunkowe renderowanie (jeśli pole istnieje i nie jest puste)
 * - {!fieldName}...{/fieldName} - negacja warunku (jeśli pole nie istnieje lub jest puste)
 */

import { applyTransforms } from './transforms';

// Regex dla warunkowego renderowania (pozytywnego)
const CONDITIONAL_REGEX = /\{\?(\w+)\}([\s\S]*?)\{\/\1\}/g;

// Regex dla warunkowego renderowania (negatywnego)
const NEGATION_REGEX = /\{!(\w+)\}([\s\S]*?)\{\/\1\}/g;

// Regex dla placeholderów z opcjonalnymi transformacjami
const PLACEHOLDER_REGEX = /\{([^}]+)\}/g;

/**
 * Sprawdź czy wartość jest "truthy" dla warunków
 * @param {*} value - wartość do sprawdzenia
 * @returns {boolean}
 */
function isTruthy(value) {
    if (value === null || value === undefined) return false;
    if (value === '') return false;
    if (value === false) return false;
    if (value === 0) return false;
    return true;
}

/**
 * Pobierz wartość z danych (obsługuje notację z kropką, np. "theme.defaultTextColor")
 * @param {object} data - obiekt z danymi
 * @param {string} path - ścieżka do wartości
 * @returns {*} - wartość lub undefined
 */
function getValueByPath(data, path) {
    if (!data || !path) return undefined;

    const parts = path.split('.');
    let value = data;

    for (const part of parts) {
        if (value === null || value === undefined) return undefined;
        value = value[part];
    }

    return value;
}

/**
 * Parsuj szablon Text DSL z danymi
 * @param {string} template - szablon z placeholderami
 * @param {object} data - dane do podstawienia
 * @returns {string} - przetworzony tekst
 */
export function parseTextDSL(template, data = {}) {
    if (!template) return '';
    if (typeof template !== 'string') return String(template);

    let result = template;

    // 1. Przetwórz warunki pozytywne {?field}...{/field}
    result = result.replace(CONDITIONAL_REGEX, (match, field, content) => {
        const value = getValueByPath(data, field);
        if (isTruthy(value)) {
            // Rekurencyjnie przetwórz zawartość
            return parseTextDSL(content, data);
        }
        return '';
    });

    // 2. Przetwórz warunki negatywne {!field}...{/field}
    result = result.replace(NEGATION_REGEX, (match, field, content) => {
        const value = getValueByPath(data, field);
        if (!isTruthy(value)) {
            // Rekurencyjnie przetwórz zawartość
            return parseTextDSL(content, data);
        }
        return '';
    });

    // 3. Podstaw placeholdery {field} lub {field|transform}
    result = result.replace(PLACEHOLDER_REGEX, (match, expression) => {
        // Podziel na pole i transformacje
        const parts = expression.split('|');
        const fieldPath = parts[0].trim();
        const transforms = parts.slice(1);

        // Pobierz wartość
        let value = getValueByPath(data, fieldPath);

        // Jeśli wartość to undefined lub null, zamień na pusty string
        if (value === undefined || value === null) {
            value = '';
        }

        // Zastosuj transformacje
        if (transforms.length > 0) {
            value = applyTransforms(String(value), transforms);
        }

        return String(value);
    });

    return result;
}

/**
 * Sprawdź czy warunek jest spełniony dla elementu
 * @param {string} condition - nazwa pola warunku
 * @param {object} data - dane
 * @returns {boolean}
 */
export function checkCondition(condition, data) {
    if (!condition) return true;
    if (condition.startsWith('!')) {
        const value = getValueByPath(data, condition.slice(1));
        return !isTruthy(value);
    }
    const value = getValueByPath(data, condition);
    return isTruthy(value);
}

/**
 * Parsuj kolor z szablonu (obsługuje {theme.defaultTextColor})
 * @param {string} colorTemplate - szablon koloru
 * @param {object} theme - obiekt theme
 * @returns {string} - kolor hex
 */
export function parseColor(colorTemplate, theme) {
    if (!colorTemplate) return '#000';

    if (colorTemplate.startsWith('{') && colorTemplate.endsWith('}')) {
        const path = colorTemplate.slice(1, -1);
        if (path.startsWith('theme.')) {
            const themePath = path.slice(6); // usuń "theme."
            return theme?.[themePath] || '#000';
        }
    }

    return colorTemplate;
}

export default {
    parseTextDSL,
    checkCondition,
    parseColor
};
